import type { Map } from "mapbox-gl";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import DateChartWrapper from "senf-atomic-design-system/src/components/core/charts/DateChartWrapper";
import { createSafeFilename, useAuthContext } from "senf-shared";
import type { Project } from "senf-shared/types";
import {
  exportChartAsPNG,
  exportChartAsSVG,
  exportMap,
} from "senf-shared/utils/exportData";
import { useCheckParticipationPhase } from "senf-shared/utils/projectHelpers";
import { cn } from "../../../util/classNameConstructor";
import BarChart from "../../atoms/charts/BarChart";
import BaseChartWrapper from "../../atoms/charts/BaseChartWrapper";
import PostcodeMap from "../../atoms/map/PostcodeMap";
import Dropdown from "../../core/dropdown/Dropdown";
import DemographicsResults from "./DemographicsResults";
import ParticipationStructure from "./ParticipationStructure";

const promptHeadline = `Bitte fasse alle Umfrageantworten zusammen und erstelle 1 aussagekräftige Headline, die in einer Zeitung veröffentlicht werden könnten. Verwende vor jeder Überschrift 'h:'.`;
const promptSummary = `Bitte fasse alle Umfrageantworten zusammen und erstelle einen 1500 Zeichen Bericht mit Handlungsempfehlungen für die Stadt. Schreibe präzise und direkt die Ergebnisse ohne den Inhalt zu umschreiben`;

export const DemographicsChartWrapper = ({
  title,
  dropdownItems,
  children,
}) => {
  return (
    <div className="col-span-6 flex min-h-[310px] flex-col gap-5 rounded-2xl border bg-white p-5 min-w-0">
      <div className="flex items-center justify-between">
        <h3 className="heading-lg">{title}</h3>
        {dropdownItems ? (
          <Dropdown
            variant="shapeless"
            trailingIcon="more"
            closeOnClick={true}
            size="lg"
            className="-my-5 -mx-2"
            items={dropdownItems}
          />
        ) : null}
      </div>
      {children}
    </div>
  );
};

const ProjectResultsPageTemplate = ({ variant, project, navigate }) => {
  const { t } = useTranslation();
  const dataKeys = {
    registrations: {
      color: "var(--category-surface-blue)",
      label: t("global:users"),
    },
    ideas: {
      color: "var(--palette-green-900)",
      label: t("global:posts"),
    },
    likes: {
      color: "var(--palette-green-700)",
      label: t("global:likes"),
    },
    comments: {
      color: "var(--palette-green-500)",
      label: t("global:comments"),
    },
    surveyEntries: {
      color: "var(--category-surface-yellow)",
      label: t("survey:completedSurveyEntries"),
    },
  };
  const allData = { ...dataKeys };

  // Only surveys
  const surveysOnly = { surveyEntries: dataKeys.surveyEntries };

  // Only ideas/likes/comments
  const ideasLikesCommentsOnly = {
    ideas: dataKeys.ideas,
    likes: dataKeys.likes,
    comments: dataKeys.comments,
  };

  const getParticipationPhase = useCheckParticipationPhase();
  const { firstStartDate, lastEndDate } = getParticipationPhase(project);

  const [loading, setLoading] = useState(false);
  const [headlines, setHeadlines] = useState([]);
  const [report, setReport] = useState(null);

  //   const handlePrepareSurveyData = () => {
  //     setLoading(true);
  //     const survey = project?.surveys?.[0];

  //     survey.pages = survey.pages.filter(
  //       ({ qtype }) => qtype !== "area" && qtype !== "path" && qtype !== "point"
  //     );

  //     const results = project?.surveyEntries.filter(
  //       (item) => item?.surveyId === survey.surveyId
  //     );
  //     const data = results?.filter(
  //       (e) => e && e.values && Object.keys(e.values).length
  //     );
  //     const numbers = false;
  //     const { responses, header } = mapResults(data, survey, numbers, t);
  //     const convertedHeaders = header
  //       .map((question, index) => `${index} = ${question}`)
  //       .join(", ");
  //     const convertedResult = responses.map((response) =>
  //       response.map((answer, index) => `${index} = ${answer}`).join(", ")
  //     );

  //     const slicedResults = convertedResult.slice(1, 15);

  //     console.log(JSON.stringify(convertedResult).length);

  //     const merged = `Die Fragen: ${JSON.stringify(
  //       convertedHeaders
  //     )} die Antworten:${JSON.stringify(slicedResults)}`;

  //     return merged;
  //   };
  //   const handleChatGptRequest = (prompt, type) => {
  //     const tokenLength = 1500;
  //     const text = handlePrepareSurveyData();
  //     chatGptRequest({
  //       text,
  //       prompt,
  //       tokenLength,
  //       type: "gpt4turbo",
  //       callback: (data) => {
  //         console.log(data);

  //         if (type === "headlines") {
  //           const resultArray = data.split("h:");
  //           setHeadlines(resultArray);
  //         } else {
  //           setReport(data);
  //         }

  //         setLoading(false);
  //       },
  //     });
  //   };
  const chartRef = useRef(null);
  const dataKeysProjectProgress =
    project?.limitations?.ideaMaps === false
      ? surveysOnly
      : project?.limitations?.surveys === false
        ? ideasLikesCommentsOnly
        : allData;

  const participationDevelopmentDropdown = variant !== "mainApp" && [
    {
      text: t("util:downloadAs", {
        val: t("global:chart"),
        format: "PNG",
      }),
      leadingIcon: "download",
      onClick: () =>
        exportChartAsPNG({
          ref: chartRef,
          fileName: `${t(
            "global:participationDevelopment",
          )}_${createSafeFilename([project?.internalProjectTitle])}`,
          downloadImmediately: true,
        }),
    },
    {
      text: t("util:downloadAs", {
        val: t("global:chart"),
        format: "SVG",
      }),
      leadingIcon: "download",
      onClick: () =>
        exportChartAsSVG({
          ref: chartRef,
          fileName: `${t(
            "global:participationDevelopment",
          )}_${createSafeFilename([project?.internalProjectTitle])}`,
          downloadImmediately: true,
        }),
    },
  ];

  return (
    <div className="flex h-screen w-screen flex-col overflow-scroll px-5">
      <section className="mx-auto mb-2 grid w-full max-w-4xl grid-cols-6 gap-6 px-5 pb-10">
        <section className="col-span-6 mb-6 mt-20  flex flex-col justify-start">
          <Button
            leadingIcon="arrowhead-left-md"
            text={variant === "mainApp" ? "Projekt-Infoseite" : "Projektseite"}
            variant="shapeless"
            className="p-0 hover:bg-transparent hover:text-black active:bg-transparent"
            onClick={() => {
              navigate(
                variant === "mainApp"
                  ? `/projects/${project.projectId}/info`
                  : `/${project.organizationId}/projects/${project.projectId}`,
              );
            }}
          />
          <h2 className="heading-3xl col-span-6">
            {t("global:generalParticipationData")}
          </h2>
        </section>

        {/* <section className="bg-surface-subdued relative  sticky top-0 z-50 col-span-6 h-20">
          <Tabs
            className="heading-2xl
            active={1}
            tabs={[
              { text: "Übersicht" },
              { text: "Demographie" },
              { text: "Ideen" },
              { text: "Meinungsbilder" },
            ]}
          />
        </section> */}
        <DemographicsResults
          project={project}
          data={project.registrations}
          downloadCharts={variant !== "mainApp"}
          className="col-span-6 grid gap-6"
        />

        <DemographicsChartWrapper
          title={t("global:participationDevelopment")}
          dropdownItems={participationDevelopmentDropdown}
        >
          <DateChartWrapper
            data={project}
            ref={chartRef}
            dataKeys={dataKeysProjectProgress}
            legend
            legendClassName="hidden [.exported-chart_&]:block"
            className="mt-3"
            height={420}
            showHeader={true}
            showToggles={true}
            startDate={firstStartDate?.getTime()}
            endDate={lastEndDate?.getTime()}
          />
        </DemographicsChartWrapper>

        {/* {user?.isSuperAdmin && (
          <>
            <div className="col-span-6 flex w-full justify-center">
              <Button
                onClick={() => {
                  handleChatGptRequest(promptHeadline, "headlines");
                  handleChatGptRequest(promptSummary, "report");
                }}
                leadingIcon="ai"
                text="Automatisch auswerten"
                loading={loading}
                variant="white"
                size="xl"
                className="shadow-lg"
                classNameWrapper="mx-auto"
              />
            </div>

            {headlines.length ? (
              <div className="col-span-6 flex min-h-[310px] flex-col gap-5 rounded-2xl bg-white p-5">
                {headlines.map((text, index) => {
                  if (index === 0) return;
                  return <span className="heading-lg">{text}</span>;
                })}

                {report}
              </div>
            ) : null}
          </>
        )} */}
      </section>
    </div>
  );
};

export default ProjectResultsPageTemplate;
